import styled from "styled-components";
import dayjs from "project-config/utils/dayjs";
import { Form, Select, Checkbox, DatePicker, Button } from "@renderbus/ui";
import { useAuthManageStore } from "@/pages/authorization-management/store/auth-manage-store";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { AuthTime } from "@/pages/authorization-management/components/auth-edit-drawer/constants";
import { useAuthEdit } from "@/pages/authorization-management/components/auth-edit-drawer/hooks/useAuthEdit";
import { AuthDetailTable } from "@/pages/authorization-management/components/auth-detail-table";
import { CustomStyleDrawer } from "./atom";

export function AuthEditDrawer({
  isEdit = false,
  isOnlyView = false,
}: {
  isEdit?: boolean;
  isOnlyView?: boolean;
}) {
  const { openEditAuthDrawer, checkAllPermission } = useAuthManageStore();
  const {
    showExpireDatePicker,
    setShowExpireDatePicker,
    form,
    disabledDate,
    checkAccountOptions,
    authTimeOptions,
    checkPlatformOptions,
    handleSubmitAuth,
    validateExpireDate,
    validateCheckedAccount,
    validateCheckedPlatform,
    handleCheckAll,
    handleCancel,
    disabledDateTime,
    onDateChange,
    isSubmitDisabled,
  } = useAuthEdit();
  const t = getTranslateNsT("center");
  const endDate = form.getFieldValue("expireDate") || dayjs(new Date());
  return (
    <CustomStyleDrawer
      width={950}
      open={openEditAuthDrawer}
      className="border-title"
      title={
        isOnlyView
          ? t("user_auth_details")
          : isEdit
          ? t("auth_manage_edit")
          : t("auth_manage_new_title")
      }
      onClose={handleCancel}
      drawerContent={
        <ContentWrapper isOnlyView={isOnlyView}>
          {!isOnlyView && (
            <TipWrapper>
              <span>{t("auth_manage_edit_tip_0")}</span>
              <span>{t("auth_manage_edit_tip_1")}</span>
            </TipWrapper>
          )}
          <Form colon={false} form={form} size={"middle"} labelAlign="right">
            <RowItem className="expire-date">
              <label>{t("auth_manage_key_expire")}</label>
              {isOnlyView ? (
                `${form.getFieldValue("startDate")}~${endDate.format(
                  "YYYY-MM-DD HH:mm:ss"
                )}`
              ) : (
                <>
                  <Form.Item
                    name="authType"
                    style={{ display: "inline-block" }}
                  >
                    <Select
                      options={authTimeOptions}
                      className="auth-edit-expire-select"
                      getPopupContainer={(triggerNode) =>
                        triggerNode.parentNode
                      }
                      allowClear={false}
                      onChange={(value: number) => {
                        setShowExpireDatePicker(value === AuthTime.Custom);
                      }}
                    />
                  </Form.Item>
                  {showExpireDatePicker && (
                    <Form.Item
                      name="expireDate"
                      className="expire-date-item"
                      style={{ display: "inline-block" }}
                      rules={[{ validator: validateExpireDate }]}
                    >
                      <DatePicker
                        format="YYYY-MM-DD HH:mm:ss"
                        type="date"
                        className="auth-edit-expire-datepicker"
                        disabledDate={disabledDate}
                        disabledTime={disabledDateTime}
                        onChange={onDateChange}
                        showTime={{
                          defaultValue: dayjs(new Date(), "HH:mm:ss"),
                        }}
                        allowClear={false}
                      />
                    </Form.Item>
                  )}
                </>
              )}
            </RowItem>
            <RowItem>
              <label>{t("user_auth_account")}</label>
              <Form.Item
                name="checkedAccount"
                rules={[{ validator: validateCheckedAccount }]}
              >
                <Checkbox.Group
                  options={checkAccountOptions}
                  disabled={isOnlyView}
                ></Checkbox.Group>
              </Form.Item>
            </RowItem>
            <RowItem>
              <label>{t("user_auth_platform")}</label>
              <Form.Item
                name="checkedPlatform"
                rules={[{ validator: validateCheckedPlatform }]}
              >
                <Checkbox.Group
                  options={checkPlatformOptions}
                  disabled={isOnlyView}
                ></Checkbox.Group>
              </Form.Item>
            </RowItem>
          </Form>
          <RowItem className={isOnlyView ? "table only-view" : "table"}>
            <Checkbox
              checked={checkAllPermission}
              onChange={handleCheckAll}
              disabled={isOnlyView}
            >
              {t("common_check_all")}
            </Checkbox>
            <AuthDetailTable className="edit" />
          </RowItem>
          {!isOnlyView && (
            <RowItem className="operation">
              <Button type="default" onClick={handleCancel}>
                {t("common_store_global_dialog_default_cancel")}
              </Button>
              <Button
                type="primary"
                onClick={handleSubmitAuth}
                disabled={isSubmitDisabled}
              >
                {isEdit ? t("user_auth_save") : t("auth_manage_new")}
              </Button>
            </RowItem>
          )}
        </ContentWrapper>
      }
    />
  );
}
const RowItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  label {
    min-width: 100px;
    text-align: end;
    margin-right: 24px;
    color: rgba(255, 255, 255, 0.85);
  }
  margin-bottom: 24px;
  &.table {
    align-items: flex-start;
    flex-direction: column;
    margin-bottom: 182px;
    > label {
      min-width: unset;
      margin: 0 0 16px 0;
      align-self: flex-end;
    }
  }
  &.only-view {
    margin-bottom: 0;
  }
  &.operation {
    margin-bottom: unset;
    justify-content: flex-end;
    gap: 16px;
  }
  &.expire-date {
    .expire-date-item {
      margin-left: 20px;
    }
  }
  .ant-form-item-explain-error {
    width: max-content;
    position: absolute;
  }
`;

const TipWrapper = styled.div`
  width: 100%;
  min-height: 70px;
  padding: 5px 0;
  background: var(--color-main-alpha-1);
  border: 1px solid var(--color-main-alpha-26);
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding-left: 16px;
  margin-bottom: 30px;
  line-height: 22px;
`;

const ContentWrapper = styled.div<{ isOnlyView: boolean }>`
  padding: ${(p) =>
    p.isOnlyView ? "24px 24px 0 24px" : "24px 24px 16px 24px"};
  color: rgba(255, 255, 255, 0.85);
  .ant-form-item {
    margin-bottom: unset;
  }
  .ant-select {
    width: 220px;
  }
  .auth-edit-expire-datepicker {
    width: 200px;
    background: #2c2c31 !important;
    .ant-picker-input {
      width: 200px !important;
    }
    .ant-picker-panel-container {
      background: #333336 !important;
    }
  }
  .ant-form-item .ant-form-item {
    margin-bottom: unset;
  }
  .ant-checkbox-group {
    label {
      margin-left: 0;
      min-width: 155px;
      color: rgba(255, 255, 255, 0.85);
    }
  }
  .ant-select-selector {
    background: #2c2c31 !important;
  }
`;
